<script setup>
import { useHelperFunctions } from '@/composables/useHelperFunctions';
import i18n from '@/plugins/i18n';
import { useNotificationStore } from '@/stores/notification';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const { humanReadableDate } = useHelperFunctions();

const notificationStore = useNotificationStore();
const router = useRouter();
const notificationRes = ref([]);
const makeReadLoading = ref(false);
const loading = ref(false);
const badge = ref(0);
const { t } = i18n.global;

onMounted(() => {
  if (router.currentRoute.value.name != 'login') {
    getUnreadMsgs();
  }
});

async function getUnreadMsgs() {
  loading.value = true;
  await notificationStore
    .getUnreadNotification()
    .then(response => {
      notificationRes.value = response.data.data;
    })
    .finally(() => {
      loading.value = false;
    });
}

async function loadNotifications() {
  loading.value = true;
  await notificationStore
    .getUserNotificationList()
    .then(response => {
      notificationRes.value = response.data.data;
    })
    .finally(() => {
      loading.value = false;
    });
}

async function clickOnNotification(item) {
  makeReadLoading.value = true;
  await notificationStore
    .makeRead(item.id, item.uuid)
    .then(() => {
      let url = '/';
      switch (item.model_type) {
        case 'export':
        case 'product':
        case 'order':
        default:
          url = '/' + item.model_type + 's';
          break;
      }
      router.push({ path: url, query: { id: item.model_id } });
    })
    .finally(() => {
      makeReadLoading.value = false;
    });
}
</script>

<template>
  <VBadge :model-value="!!badge" v-bind="badge">
    <VBtn color="on-surface" icon @click="loadNotifications">
      <VBadge :model-value="!!badge" color="error" :content="badge">
        <VProgressCircular v-if="loading" indeterminate color="primary" />
        <VIcon v-else icon="mdi-bell-outline" size="24" />
      </VBadge>

      <VMenu activator="parent" width="380px" location="bottom end" offset="14px">
        <VList v-if="notificationRes.length" class="py-0">
          <!-- 👉 Header -->
          <VListItem :title="t('notifications')" class="notification-section" height="48px">
            <template #append>
              <VChip v-if="notificationRes.length" color="default" size="small"> {{ notificationRes.length }} New </VChip>
            </template>
          </VListItem>

          <VDivider />

          <!-- 👉 Notifications list -->
          <template v-for="notification in notificationRes" :key="notification.title">
            <VListItem
              :title="notification.title"
              :subtitle="notification.description"
              link
              lines="one"
              min-height="66px"
              @click="clickOnNotification(notification)">
              <template #append>
                <small class="whitespace-no-wrap text-medium-emphasis">{{ humanReadableDate(notification.created_at) }}</small>
              </template>
            </VListItem>
            <VDivider />
          </template>

          <!-- 👉 Footer -->
          <VListItem class="notification-section">
            <VBtn block variant="text" color="default" @click="$router.push({ path: '/notifications' })">
              {{ t('viewAllNotifications') }}
            </VBtn>
          </VListItem>
        </VList>
        <VList v-else>
          <VListItem :title="t('noNotificationAvailable')" class="notification-section" height="48px" />
        </VList>
      </VMenu>
    </VBtn>
  </VBadge>
</template>

<style lang="scss">
.notification-section {
  padding: 14px !important;
}
</style>
